import React, { useState, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { signOut } from 'firebase/auth';
import { auth } from './firebaseConfig';
import Logo from './components/Logo';
import './LinkedInHeaderStyles.css';

function LinkedInHeader({ userProfile }) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [isBusinessOpen, setIsBusinessOpen] = useState(false);
  const [isMeOpen, setIsMeOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);

  const businessTimeoutRef = useRef(null);
  const meTimeoutRef = useRef(null);
  const langTimeoutRef = useRef(null);

  // Helper for avatar
  const defaultAvatar = "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0iI2NjYyI+PHBhdGggZD0iTTEyIDEyYzIuMjEgMC00LTEuNzktNC00czEuNzktNCA0LTRzNCAxLjc5IDQgNHMtMS43OSA0LTQgNHptMCAyYy0yLjY3IDAtOCAxLjM0LTggNHYyaDE2di0yYy04LTIuNjYtNS4zMy00LTgtNHoiLz48L3N2Zz4=";
  // Use avatar_url from profile (snake_case as per Firestore / MyProfile.js)
  const avatarUrl = userProfile?.avatar_url || defaultAvatar;

  const isActive = (path) => location.pathname === path ? 'active' : '';
  const roles = userProfile?.roles || [];
  const hasBusinessAccess = roles.some(role => ['admin', 'sales', 'hr', 'hm'].includes(role));

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
    setIsLangOpen(false);
  };

  // Hover Handlers
  const handleMouseEnter = (setter, timeoutRef) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setter(true);
  };

  const handleMouseLeave = (setter, timeoutRef) => {
    timeoutRef.current = setTimeout(() => {
      setter(false);
    }, 300); // 300ms delay before closing
  };

  return (
    <>
      <div className="li-header-fixed-spacer"></div>
      <header className="li-header">
        <div className="li-header-container">
          <Link to="/welcome" className="li-logo" style={{ textDecoration: 'none' }}>
            <Logo />
          </Link>

          <div className="li-search-container" style={{ width: 'auto', minWidth: '350px', paddingRight: '10px' }}>
            <div className="li-search-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="16" height="16" fill="currentColor">
                <path d="M14.56 12.44 11.3 9.18a5.51 5.51 0 1 0-2.12 2.12l3.26 3.26a1.5 1.5 0 1 0 2.12-2.12M3 6.5A3.5 3.5 0 1 1 6.5 10 3.5 3.5 0 0 1 3 6.5"></path>
              </svg>
            </div>
            <span className="li-search-text" style={{ color: '#666', fontSize: '14px', fontWeight: '500', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginLeft: '35px' }}>
              {t('header.noSearching', 'No searching. Just matching.')}
            </span>
          </div>

          <nav className="li-nav">
            <ul className="li-nav-list">
              <li className={`li-nav-item ${isActive('/welcome')}`}>
                <Link to="/welcome" className="li-nav-item">
                  <svg className="li-nav-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <path d="M23 9v2h-2v7a3 3 0 0 1-3 3h-4v-6h-4v6H6a3 3 0 0 1-3-3v-7H1V9l11-7z"></path>
                  </svg>
                  <span>{t('nav.home')}</span>
                </Link>
              </li>
              <li className={`li-nav-item ${isActive('/simulations')}`}>
                <Link to="/simulations" className="li-nav-item">
                   <svg className="li-nav-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <path d="M17 6V5a3 3 0 0 0-3-3h-4a3 3 0 0 0-3 3v1H2v4a3 3 0 0 0 3 3h14a3 3 0 0 0 3-3V6zM9 5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v1H9zm10 9a4 4 0 0 0 3-1.38V17a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3v-4.38A4 4 0 0 0 5 14z"></path>
                   </svg>
                   <span>{t('nav.simulations')}</span>
                </Link>
              </li>
              <li className={`li-nav-item ${isActive('/chat')}`}>
                <Link to="/chat?reset=true" className="li-nav-item">
                  <svg className="li-nav-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <path d="M16 4H8a7 7 0 0 0 0 14h4v4l8.16-5.39A6.78 6.78 0 0 0 23 11a7 7 0 0 0-7-7m-8 8.25A1.25 1.25 0 1 1 9.25 11 1.25 1.25 0 0 1 8 12.25m4 0A1.25 1.25 0 1 1 13.25 11 1.25 1.25 0 0 1 12 12.25m4 0A1.25 1.25 0 1 1 17.25 11 1.25 1.25 0 0 1 16 12.25"></path>
                  </svg>
                  <span>{t('nav.chat')}</span>
                </Link>
              </li>

              {/* Language Switcher */}
              <li 
                className="li-work-dropdown"
                onMouseEnter={() => handleMouseEnter(setIsLangOpen, langTimeoutRef)}
                onMouseLeave={() => handleMouseLeave(setIsLangOpen, langTimeoutRef)}
              >
                <div className="li-nav-item" style={{cursor: 'pointer'}} onClick={() => setIsLangOpen(!isLangOpen)}>
                   <svg className="li-nav-icon" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2"></circle>
                      <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" fill="none" stroke="currentColor" strokeWidth="2"></path>
                   </svg>
                   <span>{t('nav.language')}</span>
                </div>
                {isLangOpen && (
                  <div className="li-dropdown-menu">
                    <ul className="li-dropdown-list">
                      <li>
                        <button className="li-dropdown-link" onClick={() => changeLanguage('en-GB')} style={{border: 'none', background: 'none', width: '100%', textAlign: 'left', cursor: 'pointer'}}>
                          English
                        </button>
                      </li>
                      <li>
                        <button className="li-dropdown-link" onClick={() => changeLanguage('tr-TR')} style={{border: 'none', background: 'none', width: '100%', textAlign: 'left', cursor: 'pointer'}}>
                          Türkçe
                        </button>
                      </li>
                    </ul>
                  </div>
                )}
              </li>

              {/* Me / Profile Dropdown */}
              <li 
                className={`li-work-dropdown ${isActive('/profile')}`}
                onMouseEnter={() => handleMouseEnter(setIsMeOpen, meTimeoutRef)}
                onMouseLeave={() => handleMouseLeave(setIsMeOpen, meTimeoutRef)}
              >
                <div className="li-nav-item" style={{cursor: 'pointer'}} onClick={() => setIsMeOpen(!isMeOpen)}>
                  <img src={avatarUrl} alt="Me" className="li-nav-avatar" />
                  <span>
                    {t('nav.me')}
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="16" height="16" fill="currentColor" style={{marginLeft: '2px'}}>
                      <path d="M8 11 3 6h10Z"></path>
                    </svg>
                  </span>
                </div>
                {isMeOpen && (
                  <div className="li-dropdown-menu">
                    <ul className="li-dropdown-list">
                      <li>
                        <Link to="/profile" className="li-dropdown-link">
                          {t('nav.viewProfile')}
                        </Link>
                      </li>
                      <li>
                        <Link to="/agent-performance" className="li-dropdown-link">
                          {t('nav.agentPerformance')}
                        </Link>
                      </li>
                      <li>
                        <button onClick={handleLogout} className="li-dropdown-link" style={{border: 'none', background: 'none', width: '100%', textAlign: 'left', cursor: 'pointer', color: '#cc1016'}}>
                          {t('nav.signOut')}
                        </button>
                      </li>
                    </ul>
                  </div>
                )}
              </li>
              
              {hasBusinessAccess && (
                <>
                  <div className="li-nav-separator"></div>
                  <li 
                    className="li-work-dropdown"
                    onMouseEnter={() => handleMouseEnter(setIsBusinessOpen, businessTimeoutRef)}
                    onMouseLeave={() => handleMouseLeave(setIsBusinessOpen, businessTimeoutRef)}
                  >
                    <div className="li-nav-item" style={{cursor: 'pointer'}} onClick={() => setIsBusinessOpen(!isBusinessOpen)}>
                       <svg className="li-nav-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                        <path d="M2 2h2.67v2.67H2zm4.67 2.67h2.67V2H6.67zM11.34 2v2.67h2.67V2zM2 9.33h2.67V6.66H2zm4.67 0h2.67V6.66H6.67zm4.67 0h2.67v-2.67H6.67zm4.67 0h2.67v-2.67h-2.67zM2 14h2.67v-2.67H2zm4.67 0h2.67v-2.67H6.67zm4.67 0h2.67v-2.67h-2.67z"></path>
                       </svg>
                       <span>
                         {t('nav.forBusiness')}
                         <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="16" height="16" fill="currentColor" style={{marginLeft: '2px'}}>
                            <path d="M8 11 3 6h10Z"></path>
                         </svg>
                       </span>
                    </div>

                    {isBusinessOpen && (
                      <div className="li-dropdown-menu">
                        <ul className="li-dropdown-list">
                          {roles.includes('admin') && (
                            <li><Link to="/admin" className="li-dropdown-link">{t('nav.admin')}</Link></li>
                          )}
                          {roles.includes('sales') && (
                            <li><Link to="/sales-dashboard" className="li-dropdown-link">{t('nav.sales')}</Link></li>
                          )}
                          {roles.includes('hr') && (
                            <li><Link to="/hr-dashboard" className="li-dropdown-link">{t('nav.hrDashboard')}</Link></li>
                          )}
                          {roles.includes('hr') && (
                            <li><Link to="/talent-pool" className="li-dropdown-link">{t('nav.talentPool')}</Link></li>
                          )}
                          {roles.includes('hm') && (
                            <li><Link to="/start-hiring" className="li-dropdown-link">{t('nav.hiring')}</Link></li>
                          )}
                        </ul>
                      </div>
                    )}
                  </li>
                </>
              )}
            </ul>
          </nav>
        </div>
      </header>
    </>
  );
}

export default LinkedInHeader;